import React, { useMemo, useState, useEffect } from 'react';
import './MotorStatusCard.css';

// Señales EG1 según el PDF (Estrategia 1).
// El bot escribe los snapshots en /active_signals/{gid} cuando cumple las
// 4 ventanas (ver firebase_upload.py::_eg1_pdf_window_match). El hook
// useActiveSignals separa en activas vs registradas comparando snapshot.linea
// contra la línea live del pick. Cuando termina el partido, el bot borra
// el nodo → el partido desaparece de ambas colas.
//
// Ventanas:
//   Q2 min 3+   · edge ≥ 15
//   Q3 min 3+   · edge ≥ 20
//   Q4 min 1    · edge ≥ 15
//   Q4 min 6    · edge ≥ 15
// Filtro extra: línea ≥ 160. Sin filtro de liga.
const ROTATION_MS = 5000;

const MotorStatusCard = ({ games = [], totalGames = null, activeSignals = [], recordedSignals = [] }) => {
  const signals = activeSignals;
  const records = recordedSignals;
  const hasActive = signals.length > 0;
  const hasRecords = records.length > 0;

  const [signalIdx, setSignalIdx] = useState(0);
  useEffect(() => {
    if (signals.length <= 1) {
      setSignalIdx(0);
      return undefined;
    }
    const id = setInterval(() => {
      setSignalIdx((i) => (i + 1) % signals.length);
    }, ROTATION_MS);
    return () => clearInterval(id);
  }, [signals.length]);

  const activeSignal = hasActive ? signals[Math.min(signalIdx, signals.length - 1)] : null;

  const { entrarCount: entrarRaw, vigilarCount, total } = useMemo(() => {
    const t = totalGames !== null ? totalGames : games.length;
    let e = 0;
    let v = 0;
    for (const g of games) {
      // 2026-05-13: el bot motor V6 escribe `categoria_dashboard` (string)
      // y `v6.categoria` (nested) en cada game de /live_games. Esos son los
      // campos reales. Los 3 fallbacks (veredicto/recommendation/status_v6)
      // se preservan por si el schema cambia en el futuro.
      const verdict = (
        g.categoria_dashboard ||
        (g.v6 && g.v6.categoria) ||
        g.veredicto ||
        g.recommendation ||
        g.status_v6 ||
        ''
      ).toString().toUpperCase();
      if (verdict === 'ENTRAR') e += 1;
      else if (verdict === 'VIGILAR') v += 1;
    }
    return { entrarCount: e, vigilarCount: v, total: t };
  }, [games, totalGames]);

  // ENTRAR lateral muestra SOLO las señales EG1 PDF actualmente activas
  // (snapshot.linea == pick.linea). Los registros (línea cambió, ya no
  // son entradas vivas) NO se cuentan acá — están abajo con su propio
  // badge "REGISTROS · n". Así "ENTRAR n. Para entrar ahora." nunca
  // miente: si dice 3, hay 3 entradas vivas con línea válida.
  const entrarCount = signals.length;

  const state = useMemo(() => {
    if (hasActive) return 'active-signal';
    // Sin señales activas EG1 PDF → estado depende del verdict V6 nativo.
    // Si solo hay registros (records>0 sin activas), state queda 'empty'
    // y los registros se ven al pie sin headline mentiroso "para entrar ahora".
    if (entrarRaw > 0 && vigilarCount > 0) return 'both';
    if (entrarRaw > 0) return 'entrar';
    if (vigilarCount > 0) return 'vigilar';
    return 'empty';
  }, [hasActive, entrarRaw, vigilarCount]);

  const config = useMemo(() => {
    switch (state) {
      case 'active-signal': {
        const sig = activeSignal;
        const edgeSign = sig && sig.edge >= 0 ? '+' : '';
        const edgeColor = sig && sig.edge >= 0 ? 'is-over' : 'is-under';
        return {
          orbColor: 'green',
          v6Color: 'green',
          accent: 'green',
          accentText: 'Señal activa.',
          rest: '',
          sub: sig ? (
            <>
              <span className="motor-signal-game">
                <strong>{sig.partido}</strong> · {sig.liga}
              </span>
              <span className="motor-signal-meta">
                <span className="motor-signal-meta-chip">Q{sig.period} · min {sig.minQ}</span>
                <span className={`motor-signal-meta-chip ${edgeColor}`}>
                  edge {edgeSign}{sig.edge.toFixed(1)}
                </span>
                <span className="motor-signal-meta-chip">línea {sig.linea.toFixed(1)}</span>
              </span>
            </>
          ) : null,
        };
      }
      case 'entrar':
        return {
          orbColor: 'green',
          v6Color: 'green',
          accent: 'green',
          accentText: `${entrarCount} ${entrarCount === 1 ? 'listo.' : 'listos.'}`,
          rest: 'Para entrar ahora.',
          sub: <>Procesando <strong>{total} partidos</strong> en vivo.</>,
        };
      case 'vigilar':
        return {
          orbColor: 'yellow',
          v6Color: 'yellow',
          accent: 'yellow',
          accentText: `${vigilarCount} cerca.`,
          rest: 'Mantén el ojo puesto.',
          sub: <>Procesando <strong>{total} partidos</strong> en vivo.</>,
        };
      case 'both':
        return {
          orbColor: 'green',
          v6Color: 'green',
          accent: 'green',
          accentText: `${entrarCount} ${entrarCount === 1 ? 'listo.' : 'listos.'}`,
          rest: `Y ${vigilarCount} más en camino.`,
          sub: <>Procesando <strong>{total} partidos</strong> en vivo.</>,
        };
      default:
        return {
          orbColor: 'edge',
          v6Color: 'edge',
          accent: 'edge',
          accentText: 'Escaneando.',
          rest: 'Sin acción todavía.',
          sub: <>Procesando <strong>{total} partidos</strong> en vivo.</>,
        };
    }
  }, [state, entrarCount, vigilarCount, total, activeSignal]);

  return (
    <div className={`motor-status-card motor-state-${state}`}>
      <div className="motor-mesh" aria-hidden="true">
        <div className="motor-blob b1" />
        <div className="motor-blob b2" />
        <div className="motor-blob b3" />
      </div>
      <div className="motor-card-inner">
        <div className="motor-orb-section">
          <div className={`motor-orb motor-orb-${config.orbColor}`} aria-hidden="true">
            <div className="motor-orb-layer l3" />
            <div className="motor-orb-layer l2" />
            <div className="motor-orb-layer l1" />
            <div className="motor-orb-layer core" />
          </div>
          <div className="motor-info">
            <span className={`motor-v6 motor-v6-${config.v6Color}`}>MOTOR V6.1 · LIVE</span>
            <h2 className="motor-headline">
              <span className={`motor-accent motor-accent-${config.accent}`}>{config.accentText}</span>
              {config.rest ? <> {config.rest}</> : null}
            </h2>
            {config.sub ? (
              <p
                className="motor-sub"
                key={activeSignal ? activeSignal.id : 'static'}
              >
                {config.sub}
              </p>
            ) : null}
            {state === 'active-signal' && signals.length > 1 ? (
              <div className="motor-signal-dots" aria-hidden="true">
                {signals.map((s, i) => (
                  <span
                    key={s.id}
                    className={`motor-signal-dot ${i === signalIdx ? 'is-active' : ''}`}
                  />
                ))}
              </div>
            ) : null}
          </div>
        </div>
        <div className="motor-stats">
          <div className="motor-stat">
            <span className="motor-stat-label">
              <span className="motor-stat-dot motor-stat-dot-green" />
              Entrar
            </span>
            <span className={`motor-stat-num ${entrarCount === 0 ? 'is-zero' : 'is-green'}`}>{entrarCount}</span>
          </div>
          <div className="motor-stat">
            <span className="motor-stat-label">
              <span className="motor-stat-dot motor-stat-dot-yellow" />
              Vigilar
            </span>
            <span className={`motor-stat-num ${vigilarCount === 0 ? 'is-zero' : 'is-yellow'}`}>{vigilarCount}</span>
          </div>
        </div>
      </div>

      {hasRecords ? (
        <div className="motor-records">
          <div className="motor-records-header">
            <span className="motor-records-title">Registros · partido en curso</span>
            <span className="motor-records-count">{records.length}</span>
          </div>
          <ul className="motor-records-list">
            {records.map((r) => {
              const sign = r.edge >= 0 ? '+' : '';
              const direction = r.edge >= 0 ? 'OVER' : 'UNDER';
              const dirClass = r.edge >= 0 ? 'is-over' : 'is-under';
              // "ahora" muestra el state live del partido (clock + período)
              // para diferenciar del snapshot del primer disparo
              let nowLabel = '';
              if (r.nowClock === 'FIN') nowLabel = 'FIN';
              else if (r.nowPeriod) nowLabel = `Q${r.nowPeriod}${r.nowClock ? ` · ${r.nowClock}` : ''}`;
              return (
                <li key={r.id} className="motor-records-item">
                  <span className="motor-records-game">
                    <strong>{r.partido}</strong> · {r.liga}
                    <span className={`motor-records-direction ${dirClass}`}>{direction}</span>
                    {nowLabel ? (
                      <span className={`motor-records-now ${r.nowClock === 'FIN' ? 'is-fin' : ''}`}>
                        ahora · {nowLabel}
                      </span>
                    ) : null}
                  </span>
                  <span className="motor-records-meta">
                    <span>Q{r.period} · min {r.minQ}</span>
                    <span>edge {sign}{r.edge.toFixed(1)}</span>
                    <span>línea {r.linea.toFixed(1)}</span>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export default MotorStatusCard;
